// reportsApi.ts — Insurance Claims Phase 7 (receivables & reports, wireframe
// S8): typed client + hooks for /api/claims/reports/*. Branch-scoped like
// every claims read (routes/claims-reports.js filters on the active branch),
// so the keys are branchKey()s.
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/queryKeys';
import { authFetch } from '@/api';

export type AgeingBucket = '0_30' | '31_60' | '61_90' | '90_plus';
export interface AgeingRow {
  key: string | number;
  label: string;
  claims: number;
  buckets: Record<AgeingBucket, number>;
  total_due: number;
  open_queries: number;
  avg_settle_days: number | null;
  disallowance_pct: number | null;
}
export interface AgeingReport {
  as_on: string;
  basis: 'discharge' | 'submission';
  group_by: 'payer' | 'mode';
  mode: string | null;
  rows: AgeingRow[];
  totals: { claims: number; buckets: Record<AgeingBucket, number>; total_due: number; open_queries: number };
}
export interface AgeingParams { as_on?: string; basis?: 'discharge' | 'submission'; group_by?: 'payer' | 'mode'; mode?: string }

export interface TatGroup { target_min: number; decided: number; median_min: number | null; within_target: number; pct_within_target: number | null; max_min: number | null }
export interface TatReport { from: string; to: string; preauth: TatGroup; final: TatGroup }

export interface QueryBacklogRow {
  id: number; claim_id: number; stage: string; received_at: string; due_at: string | null; query_text: string;
  claim_ref: string; claim_status: string; payer_name: string | null; patient_name: string | null; uhid: string | null;
  overdue: boolean; age_hours: number;
}
export interface QueryBacklog { open: number; overdue: number; rows: QueryBacklogRow[] }

export interface DisallowanceRow {
  reason_code: string; count: number; amount: number; at_authorization: number; recovered: number; written_off: number; appealed: number; heads: string[];
}
export interface DisallowanceReport { from: string; to: string; rows: DisallowanceRow[]; total: number }

export interface SettledVsBilledRow {
  key: string | number; label: string; claims: number; billed: number; gross_settled: number; tds: number; net_received: number;
  deductions: number; short: number; settled_pct: number | null;
}
export interface SettledVsBilledReport {
  from: string; to: string; rows: SettledVsBilledRow[];
  totals: { claims: number; billed: number; gross_settled: number; tds: number; net_received: number; deductions: number; short: number };
}

export interface TdsRow {
  id: number; received_on: string; utr: string | null; gross_approved: string; tds: string; net_received: string;
  claim_ref: string; external_claim_no: string | null; payer_name: string | null; payer_code: string | null;
  patient_name: string | null; uhid: string | null; tds_pct: number | null;
}
export interface TdsReport { from: string; to: string; rows: TdsRow[]; totals: { gross_approved: number; tds: number; net_received: number } }

export interface RangeParams { from?: string; to?: string }

const BASE = '/claims/reports';
type QueryValue = string | number | boolean;
const qs = (p: object): Record<string, QueryValue> =>
  Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== '')) as Record<string, QueryValue>;

export function useAgeingReport(params: AgeingParams) {
  return useQuery({
    queryKey: keys.claims.report('ageing', params as Record<string, unknown>),
    queryFn: (): Promise<AgeingReport> => api.get(`${BASE}/ageing`, { query: qs(params) }),
  });
}
export function useTatReport(params: RangeParams) {
  return useQuery({
    queryKey: keys.claims.report('tat', params as Record<string, unknown>),
    queryFn: (): Promise<TatReport> => api.get(`${BASE}/tat`, { query: qs(params) }),
  });
}
export function useQueryBacklog() {
  return useQuery({
    queryKey: keys.claims.report('queries', {}),
    queryFn: (): Promise<QueryBacklog> => api.get(`${BASE}/queries`),
  });
}
export function useDisallowanceReport(params: RangeParams) {
  return useQuery({
    queryKey: keys.claims.report('disallowances', params as Record<string, unknown>),
    queryFn: (): Promise<DisallowanceReport> => api.get(`${BASE}/disallowances`, { query: qs(params) }),
  });
}
export function useSettledVsBilled(params: RangeParams) {
  return useQuery({
    queryKey: keys.claims.report('settled-vs-billed', params as Record<string, unknown>),
    queryFn: (): Promise<SettledVsBilledReport> => api.get(`${BASE}/settled-vs-billed`, { query: qs(params) }),
  });
}
export function useTdsReport(params: RangeParams) {
  return useQuery({
    queryKey: keys.claims.report('tds', params as Record<string, unknown>),
    queryFn: (): Promise<TdsReport> => api.get(`${BASE}/tds`, { query: qs(params) }),
  });
}

/** Authenticated CSV download (the app is token-auth, so a plain link would
 * 401) — same blob + detached <a download> approach as the admin CSVs. */
export async function downloadReportCsv(report: 'ageing' | 'tds', params: object, filename: string): Promise<void> {
  const search = new URLSearchParams({ ...Object.fromEntries(Object.entries(qs(params)).map(([k, v]) => [k, String(v)])), format: 'csv' }).toString();
  const res = await authFetch(`/api${BASE}/${report}?${search}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blobUrl = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}
